import type { QmFunnelStage } from "@/lib/validation";

interface QmFunnelBarsProps {
  stages: QmFunnelStage[];
  entered: number;
  selectedKey?: string | null;
  onSelectStage?: (key: string) => void;
  emptyMessage?: string;
}

const VISITED_HINT =
  "Sessions that hit this page in any order; can exceed the sequential count — this is why the legacy funnel showed >100%.";

export function QmFunnelBars({
  stages,
  entered,
  selectedKey,
  onSelectStage,
  emptyMessage = "No funnel data in the selected range",
}: QmFunnelBarsProps) {
  if (stages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle px-3 py-8 text-center text-sm text-fg-faint">
        {emptyMessage}
      </div>
    );
  }

  const baseline = Math.max(1, entered);

  return (
    <ol className="flex flex-col gap-2">
      {stages.map((stage, index) => {
        const pctOfEntered = (stage.reached / baseline) * 100;
        const barWidth = Math.min(100, pctOfEntered);
        const selectable = Boolean(onSelectStage) && stage.dropOff > 0;
        const isSelected = selectedKey === stage.key;
        const displayLabel = `${index + 1}. ${stage.label}`;

        return (
          <li key={stage.key} className="flex flex-col gap-1">
            <div
              role={selectable ? "button" : undefined}
              tabIndex={selectable ? 0 : undefined}
              onClick={selectable ? () => onSelectStage!(stage.key) : undefined}
              onKeyDown={
                selectable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectStage!(stage.key);
                      }
                    }
                  : undefined
              }
              className={`flex flex-col gap-1 rounded-md px-2 py-1.5 transition-colors ${
                selectable ? "cursor-pointer hover:bg-surface-raised/40" : ""
              } ${isSelected ? "bg-surface-raised/60 ring-1 ring-info/30" : ""}`}
            >
              <div className="grid grid-cols-[1fr_3.5rem_4rem_4rem] items-baseline gap-x-3 text-xs">
                <div
                  className="truncate text-neutral-text-strong"
                  title={`${stage.label} (${stage.key})`}
                >
                  {displayLabel}
                </div>
                <div className="text-right tabular-nums text-fg-muted">
                  {pctOfEntered.toFixed(0)}%
                </div>
                <div className="text-right tabular-nums text-neutral-text-strong">
                  {stage.reached.toLocaleString("en-US")}
                </div>
                <div className="text-right text-[11px] tabular-nums text-fg-faint">
                  {index === 0 ? "—" : `${stage.conversionFromPrevPct.toFixed(0)}%`}
                </div>
              </div>

              <div
                className="h-4 min-w-0 overflow-hidden rounded-md bg-info/10"
                role="progressbar"
                aria-label={`${displayLabel}: ${stage.reached.toLocaleString("en-US")} reached, ${pctOfEntered.toFixed(0)}% of entered`}
                aria-valuenow={stage.reached}
                aria-valuemin={0}
                aria-valuemax={baseline}
              >
                <div
                  className="h-full rounded-md bg-info/70"
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              <div className="flex items-center justify-between gap-2 text-[11px] text-fg-faint">
                <span title={VISITED_HINT} className="cursor-help">
                  visited {stage.visited.toLocaleString("en-US")} hits (any-order)
                </span>
                {selectable && (
                  <span className="text-info-text">View drop-offs →</span>
                )}
              </div>
            </div>

            {stage.dropOff > 0 ? (
              <p className="py-0.5 text-center text-[11px] text-danger-text">
                ↓ {stage.dropOff.toLocaleString("en-US")} left here
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
